/**
 * SocialPlatformSettings Component
 *
 * Connect and manage OAuth for Facebook, Instagram, and TikTok.
 */
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Facebook,
  Instagram,
  Music2,
  Link2,
  Unlink,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Settings,
  ArrowLeft,
} from 'lucide-react';

import { PlatformConnectionSkeleton } from './SocialPostSkeleton';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { social, facebook } from '@/services/api/admin';

const PLATFORMS = [
  {
    id: 'facebook',
    label: 'Facebook',
    icon: Facebook,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/30',
    description: 'Post to your Facebook Page',
  },
  {
    id: 'instagram',
    label: 'Instagram',
    icon: Instagram,
    color: 'text-pink-600 dark:text-pink-400',
    bgColor: 'bg-pink-50 dark:bg-pink-900/30',
    description: 'Post to your Instagram Business account',
  },
  {
    id: 'tiktok',
    label: 'TikTok',
    icon: Music2,
    color: 'text-black dark:text-white',
    bgColor: 'bg-gray-100 dark:bg-gray-800',
    description: 'Post videos to TikTok',
  },
];

const PlatformConnection = ({
  platform,
  connection,
  onConnect,
  onDisconnect,
  onRefresh,
  onTest,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const { toast } = useToast();

  const Icon = platform.icon;
  const isConnected = connection?.is_active;
  const isExpiringSoon =
    connection?.token_expires_at &&
    new Date(connection.token_expires_at) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

  const handleConnect = async () => {
    setIsLoading(true);
    try {
      const result = await onConnect(platform.id);
      if (result?.auth_url) {
        window.location.href = result.auth_url;
      }
    } catch (error) {
      toast({
        title: 'Connection Failed',
        description: error.message || 'Failed to start OAuth flow',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setIsLoading(true);
    try {
      await onDisconnect(connection.id);
      setShowDisconnectDialog(false);
      toast({
        title: 'Disconnected',
        description: `${platform.label} has been disconnected`,
      });
    } catch (error) {
      toast({
        title: 'Disconnect Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      await onRefresh(connection.id);
      toast({
        title: 'Token Refreshed',
        description: `${platform.label} token has been refreshed`,
      });
    } catch (error) {
      toast({
        title: 'Refresh Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTest = async () => {
    setIsLoading(true);
    setTestResult(null);
    try {
      const result = await onTest(connection.id);
      setTestResult({ success: true, ...result });
    } catch (error) {
      setTestResult({ success: false, error: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <>
      <Card className={cn(isConnected && 'border-green-200 dark:border-green-800')}>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className={cn('p-2 rounded-lg', platform.bgColor)}>
              <Icon className={cn('h-6 w-6', platform.color)} />
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg flex items-center gap-2">
                {platform.label}
                {isConnected && (
                  <Badge variant="secondary" className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Connected
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>{platform.description}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isConnected ? (
            <div className="space-y-4">
              {/* Account info */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <Avatar>
                  <AvatarImage src={connection.profile_picture_url} />
                  <AvatarFallback>
                    {connection.account_name?.charAt(0) || '?'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{connection.account_name}</p>
                  {connection.account_handle && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">@{connection.account_handle}</p>
                  )}
                  {connection.page_name && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">Page: {connection.page_name}</p>
                  )}
                </div>
              </div>

              {/* Token status */}
              {connection.token_expires_at && (
                <div
                  className={cn(
                    'text-sm flex items-center gap-2 p-2 rounded-md',
                    isExpiringSoon
                      ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300'
                      : 'text-gray-500 dark:text-gray-400'
                  )}
                >
                  {isExpiringSoon && <AlertCircle className="h-4 w-4" />}
                  Token expires: {formatDate(connection.token_expires_at)}
                </div>
              )}

              {/* Last error */}
              {connection.last_error && (
                <div className="text-sm bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300 p-2 rounded-md flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  {connection.last_error}
                </div>
              )}

              {/* Test result */}
              {testResult && (
                <div
                  className={cn(
                    'text-sm p-2 rounded-md',
                    testResult.success
                      ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                      : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'
                  )}
                >
                  {testResult.success
                    ? 'Connection test successful!'
                    : `Test failed: ${testResult.error}`}
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleTest}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  Test Connection
                </Button>
                {platform.id === 'tiktok' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={isLoading}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh Token
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleConnect}
                  disabled={isLoading}
                >
                  <Link2 className="h-4 w-4 mr-2" />
                  Reconnect
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDisconnectDialog(true)}
                  disabled={isLoading}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20"
                >
                  <Unlink className="h-4 w-4 mr-2" />
                  Disconnect
                </Button>
              </div>
            </div>
          ) : (
            <Button onClick={handleConnect} disabled={isLoading} className="w-full">
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Link2 className="h-4 w-4 mr-2" />
              )}
              Connect {platform.label}
            </Button>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={showDisconnectDialog} onOpenChange={setShowDisconnectDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect {platform.label}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the connection to your {platform.label} account. You
              won't be able to publish to {platform.label} until you reconnect.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisconnect}
              disabled={isLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

const SocialPlatformSettings = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [connections, setConnections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [autoAckEnabled, setAutoAckEnabled] = useState(false);
  const [autoAckSaving, setAutoAckSaving] = useState(false);
  const { toast } = useToast();

  const fetchConnections = async () => {
    try {
      const data = await social.listPlatforms();
      setConnections(data);
    } catch (err) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to load platform connections',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAutoAck = async () => {
    try {
      const data = await facebook.autoAck.get();
      setAutoAckEnabled(!!data.auto_ack_enabled);
    } catch {
      // Not fatal; settings collection may not exist yet
    }
  };

  const handleAutoAckToggle = async (checked) => {
    setAutoAckSaving(true);
    try {
      await facebook.autoAck.update(checked);
      setAutoAckEnabled(checked);
      toast({
        title: checked ? 'Auto-acknowledgments ON' : 'Auto-acknowledgments OFF',
      });
    } catch (err) {
      toast({
        title: 'Could not update auto-ack',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setAutoAckSaving(false);
    }
  };

  useEffect(() => {
    fetchConnections();
    fetchAutoAck();

    // Check for success callback from OAuth
    const successPlatform = searchParams.get('success');
    if (successPlatform) {
      const platformName = successPlatform.charAt(0).toUpperCase() + successPlatform.slice(1);
      toast({
        title: 'Platform Connected',
        description: `${platformName} has been successfully connected!`,
      });
      // Remove the success param from URL
      setSearchParams({});
    }
  }, []);

  const getConnectionForPlatform = (platformId) =>
    connections.find((c) => c.platform === platformId);

  const handleConnect = async (platformId) => {
    return await social.connectPlatform(platformId);
  };

  const handleDisconnect = async (connectionId) => {
    await social.disconnectPlatform(connectionId);
    await fetchConnections();
  };

  const handleRefresh = async (connectionId) => {
    await social.refreshPlatformToken(connectionId);
    await fetchConnections();
  };

  const handleTest = async (connectionId) => {
    return await social.testPlatformConnection(connectionId);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="h-6 w-6" />
            Platform Connections
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Connect your social media accounts to publish posts directly from this
            dashboard.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <PlatformConnectionSkeleton />
          <PlatformConnectionSkeleton />
          <PlatformConnectionSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin/outreach/social')}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Posts
          </Button>
        </div>
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="h-6 w-6" />
            Platform Connections
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Connect your social media accounts to publish posts directly from this
            dashboard.
          </p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {PLATFORMS.map((platform) => (
          <PlatformConnection
            key={platform.id}
            platform={platform}
            connection={getConnectionForPlatform(platform.id)}
            onConnect={handleConnect}
            onDisconnect={handleDisconnect}
            onRefresh={handleRefresh}
            onTest={handleTest}
          />
        ))}
      </div>

      {/* Facebook auto-ack DM toggle (OWZ-32) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Facebook Messenger auto-acknowledgment</CardTitle>
          <CardDescription>
            When ON, inbound Page DMs trigger one automatic reply per
            conversation per 24-hour window so prospects know we received the
            message.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <Switch
              checked={autoAckEnabled}
              onCheckedChange={handleAutoAckToggle}
              disabled={autoAckSaving}
              id="auto-ack-toggle"
            />
            <Label htmlFor="auto-ack-toggle">
              Pages messaging auto-ack {autoAckEnabled ? '(ON)' : '(OFF)'}
            </Label>
          </div>
        </CardContent>
      </Card>

      {/* Help text */}
      <Card className="bg-gray-50 dark:bg-gray-800/50 border-dashed">
        <CardContent className="p-4">
          <h3 className="font-medium mb-2">Setup Instructions</h3>
          <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
            <li>
              <strong>Per-User Connections:</strong> Each admin must connect their own accounts.
              Posts you create will publish using your connections. Other admins need their own.
            </li>
            <li>
              <strong>Facebook/Instagram:</strong> You'll need a Facebook Page and an
              Instagram Business account linked to it.
            </li>
            <li>
              <strong>TikTok:</strong> Connect your TikTok account. Note: Tokens expire
              daily and can be refreshed.
            </li>
            <li>
              <strong>API Keys:</strong> Must be configured in Settings → API Keys before connecting.
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default SocialPlatformSettings;
